<template>
  <div id="integrationMCP" class="margin-subpages">
    <div class="sub-title">MCP ACCESS TOKENS</div>
    <div class="line-style my-16" />
    <div class="sub-description">
      Use Happily MCP to ask Claude Desktop questions about your team
      (e.g. <em>"what do you know about me?"</em>,
      <em>"prepare a 1:1 brief"</em>).
      Generate a token below, paste the JSON into Claude Desktop's
      config, then restart Claude. Each token sees only what you can
      see — your role and scope are decided automatically based on
      your profile.
    </div>

    <div class="flex mt-24">
      <el-button
        type="primary"
        :loading="loading"
        @click="onGenerate"
      >
        Generate new token
      </el-button>
    </div>

    <div class="section-header mt-32">
      <div class="sub-title">Your active tokens</div>
      <el-checkbox
        v-model="includeRevoked"
        @change="onToggleIncludeRevoked"
      >Show revoked</el-checkbox>
    </div>
    <div class="line-style my-16" />

    <el-table
      v-loading="loading"
      :data="tokens"
      stripe
      empty-text="No tokens yet — click Generate to mint your first one"
      style="width: 100%; margin-top: 12px"
    >
      <el-table-column label="Hash" width="140">
        <template slot-scope="scope">
          <code class="hash-mono">{{ scope.row.hash_short }}…</code>
        </template>
      </el-table-column>
      <el-table-column label="Role" width="120">
        <template slot-scope="scope">
          <el-tag
            size="small"
            :type="roleTagType(scope.row.role)"
          >{{ scope.row.role }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Scope" width="100">
        <template slot-scope="scope">
          <span>{{ scope.row.scope }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Source" width="120">
        <template slot-scope="scope">
          <span class="muted">{{ scope.row.source || 'cli' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Created" width="180">
        <template slot-scope="scope">
          <span>{{ formatDate(scope.row.created_at) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Status" width="120">
        <template slot-scope="scope">
          <el-tag
            v-if="scope.row.revoked"
            size="small"
            type="info"
          >revoked</el-tag>
          <el-tag
            v-else
            size="small"
            type="success"
          >active</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Note">
        <template slot-scope="scope">
          <span class="muted">{{ scope.row.note }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="120" align="right">
        <template slot-scope="scope">
          <el-button
            v-if="!scope.row.revoked"
            type="text"
            size="small"
            class="revoke-btn"
            @click="onRevoke(scope.row)"
          >Revoke</el-button>
        </template>
      </el-table-column>
    </el-table>

    <p v-if="error" class="error-text">⚠ {{ error }}</p>

    <!-- Modal: shown right after a successful mint -->
    <el-dialog
      :visible.sync="showMintedDialog"
      title="Your new MCP token"
      width="560px"
      top="8vh"
      custom-class="app-integration-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      @close="onCloseMintedDialog"
    >
      <div v-if="lastMinted">
        <div class="mcp-callout">
          <i class="el-icon-warning-outline"></i>
          <span>
            <strong>Save this now.</strong>
            The raw token is shown only once and can't be retrieved again.
          </span>
        </div>

        <div class="kv-block">
          <div class="kv-row">
            <span class="kv-label">For</span>
            <span class="kv-value">{{ lastMinted.email }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">Role / scope</span>
            <span class="kv-value">
              <el-tag size="small" :type="roleTagType(lastMinted.role)">
                {{ lastMinted.role }}
              </el-tag>
              <span class="muted" style="margin-left: 8px">
                {{ lastMinted.scope }}
              </span>
            </span>
          </div>
          <div class="kv-row">
            <span class="kv-label">Hash</span>
            <span class="kv-value">
              <code class="hash-mono">{{ lastMinted.hash }}</code>
            </span>
          </div>
        </div>

        <div class="qd-eyebrow mcp-section-label">Claude Desktop config</div>
        <p class="mcp-section-help">
          Paste into
          <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>,
          then fully quit (Cmd+Q) and reopen Claude Desktop.
        </p>
        <div class="config-block">
          <pre class="config-pre">{{ prettyConfig }}</pre>
          <el-button
            size="small"
            type="primary"
            class="copy-btn"
            @click="copyConfig"
          >{{ copyLabel }}</el-button>
        </div>

        <div class="qd-eyebrow mcp-section-label">Raw token</div>
        <div class="config-block">
          <pre class="config-pre token-pre">{{ lastMinted.token }}</pre>
          <el-button
            size="small"
            type="primary"
            class="copy-btn"
            @click="copyToken"
          >{{ copyTokenLabel }}</el-button>
        </div>
      </div>
      <span slot="footer">
        <el-button type="primary" @click="onCloseMintedDialog">Done</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'McpTokensPage',
  data() {
    return {
      includeRevoked: false,
      copyLabel: 'Copy config',
      copyTokenLabel: 'Copy token',
    }
  },
  computed: {
    ...mapState('mcpTokens', ['tokens', 'loading', 'error', 'lastMinted']),
    showMintedDialog: {
      get() { return this.lastMinted !== null },
      set(v) { if (!v) this.clearLastMinted() },
    },
    prettyConfig() {
      if (!this.lastMinted) return ''
      return JSON.stringify(this.lastMinted.config, null, 2)
    },
  },
  methods: {
    ...mapActions('mcpTokens', [
      'fetchMyTokens',
      'mintToken',
      'revokeToken',
      'clearLastMinted',
    ]),

    formatDate(iso) {
      if (!iso) return '—'
      try {
        const d = new Date(iso)
        return d.toLocaleString(undefined, {
          year: 'numeric', month: 'short', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      } catch { return iso }
    },

    roleTagType(role) {
      switch (role) {
        case 'admin': return 'danger'
        case 'leadership': return 'warning'
        case 'manager': return 'success'
        case 'ic':
        default: return 'info'
      }
    },

    async onGenerate() {
      try {
        await this.mintToken({})
      } catch (err) {
        if (this.$message && this.$message.error) {
          this.$message.error(`Could not mint token: ${this.error || (err && err.message) || ''}`)
        }
      }
    },

    onToggleIncludeRevoked(val) {
      this.fetchMyTokens({ includeRevoked: val })
    },

    onRevoke(row) {
      this.$confirm(
        `Revoke this token? Anyone with the raw token will lose access immediately. (hash starts with ${row.hash_short})`,
        'Revoke token',
        { confirmButtonText: 'Revoke', cancelButtonText: 'Cancel', type: 'warning' }
      ).then(async () => {
        try {
          await this.revokeToken({ hash: row.hash })
          if (this.$message && this.$message.success) {
            this.$message.success('Token revoked')
          }
        } catch (err) {
          if (this.$message && this.$message.error) {
            this.$message.error(`Revoke failed: ${this.error || (err && err.message) || ''}`)
          }
        }
      }).catch(() => { /* cancelled */ })
    },

    async copyConfig() {
      try {
        await navigator.clipboard.writeText(this.prettyConfig)
        this.copyLabel = 'Copied ✓'
        setTimeout(() => { this.copyLabel = 'Copy config' }, 2000)
      } catch (e) {
        if (this.$message && this.$message.warning) {
          this.$message.warning('Could not access clipboard — select and copy manually')
        }
      }
    },

    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.lastMinted.token)
        this.copyTokenLabel = 'Copied ✓'
        setTimeout(() => { this.copyTokenLabel = 'Copy token' }, 2000)
      } catch (e) {
        if (this.$message && this.$message.warning) {
          this.$message.warning('Could not access clipboard — select and copy manually')
        }
      }
    },

    onCloseMintedDialog() {
      this.clearLastMinted()
    },
  },

  mounted() {
    this.fetchMyTokens({ includeRevoked: this.includeRevoked }).catch(() => {
      // error already in state — UI will surface it
    })
  },
}
</script>

<style scoped lang="scss">
.sub-description {
  color: #3b3b3b;
  font-size: 14px;
  line-height: 1.6;
}
.sub-description em {
  color: #6C30CC;
  font-style: italic;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.hash-mono {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
  background: #f5f5fa;
  padding: 2px 6px;
  border-radius: 4px;
  color: #555;
}

.muted {
  color: #8a8a8a;
  font-size: 13px;
}

.revoke-btn {
  color: #CC3030;
}

.error-text {
  color: #CC3030;
  margin-top: 12px;
  font-size: 13px;
}

.mcp-callout {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff8e6;
  border: 1px solid #f7e3a6;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 20px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b5a1e;
}
.mcp-callout i {
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 16px;
  color: #F7B924;
}
.mcp-callout strong {
  color: #3b3b3b;
}

.kv-block {
  background: #fafbfd;
  border: 1px solid #eef0f5;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
}
.kv-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
}
.kv-label {
  width: 110px;
  color: #8a8a8a;
  font-weight: 500;
}
.kv-value {
  flex: 1;
  color: #2c2c2c;
}

.mcp-section-label {
  margin: 22px 0 6px;
}

.mcp-section-help {
  margin: 0 0 8px;
  color: #8a8a8a;
  font-size: 12px;
  line-height: 1.5;
}
.mcp-section-help code {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 11px;
  background: #f5f5fa;
  padding: 1px 5px;
  border-radius: 4px;
  color: #555;
}

.config-block {
  position: relative;
  background: #1e1e2e;
  border-radius: 8px;
  margin-top: 8px;
}

.config-pre {
  margin: 0;
  padding: 16px 20px;
  padding-right: 110px;
  color: #d8d8d8;
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.token-pre {
  color: #AECFFE;
}

.copy-btn {
  position: absolute;
  top: 10px;
  right: 10px;
}
</style>

<style lang="scss">
/* Pin the primary action to the brand purple. The app's `el-button--primary`
   is otherwise non-deterministic here — legacy non-scoped overrides in
   rewards/settings/questions leak a teal `#32c2d5` once those pages mount. */
#integrationMCP .el-button--primary {
  background-color: #6c30cc;
  border-color: #6c30cc;
}
#integrationMCP .el-button--primary:hover,
#integrationMCP .el-button--primary:focus {
  background-color: #522698;
  border-color: #522698;
}
</style>
